import StatPerson from "@/assets/stat-person.svg";
import StatNotepad from "@/assets/stat-notepad.svg";
import StatNotebook from "@/assets/stat-notebook.svg";
import StatPcBook from "@/assets/stat-pc-book.svg";
import type { ApiKpiStat } from "../types";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface StatCardsProps {
  isLoading: boolean;
  data: {
    kpis: ApiKpiStat | null;
  };
}

const StatCards = ({ isLoading, data }: StatCardsProps) => {
  const { t } = useTranslation();

  const formatPercentage = (value: number | null) =>
    value === null || value === 0 ? "--" : `${value}%`;

  const evolutionIcon = (
    previousValue?: number | null,
    currentValue?: number | null,
    evolutionPercent?: number | null,
  ) => {
    if (evolutionPercent === null) return null;
    if (Number(previousValue) < Number(currentValue)) {
      return <ArrowUp size={16} />;
    } else {
      return <ArrowDown size={16} />;
    }
  };

  if (isLoading) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* TOTAL DILEMMAS */}
      <div className="p-4 bg-[#39CEF3] relative flex flex-col gap-y-2 isolate rounded-md overflow-hidden">
        <p>{t("modules.stats.stat_cards.total_dilemmas")}</p>
        <span className="text-3xl font-bold">
          {data.kpis?.total_dilemmas.current}
        </span>
        <p className="text-sm flex items-center gap-1">
          {evolutionIcon(
            data.kpis?.total_dilemmas.previous,
            data.kpis?.total_dilemmas.current,
            data.kpis?.total_dilemmas.evolution_percent,
          )}
          {formatPercentage(
            Number(data.kpis?.total_dilemmas.evolution_percent),
          )}
        </p>
        <img src={StatNotepad} className="absolute bottom-0 right-0 -z-10" />
      </div>
      {/* PUBLISHED DILEMMAS */}
      <div className="p-4 bg-[#FFD400] relative flex flex-col gap-y-2 isolate rounded-md overflow-hidden">
        <p>{t("modules.stats.stat_cards.published_dilemmas")}</p>
        <span className="text-3xl font-bold">
          {data.kpis?.total_published_dilemmas.current}
        </span>
        <p className="text-sm flex items-center gap-1">
          {evolutionIcon(
            data.kpis?.total_published_dilemmas.previous,
            data.kpis?.total_published_dilemmas.current,
            data.kpis?.total_published_dilemmas.evolution_percent,
          )}
          {formatPercentage(
            Number(data.kpis?.total_published_dilemmas.evolution_percent),
          )}
        </p>
        <img src={StatPcBook} className="absolute bottom-0 right-0 -z-10" />
      </div>
      {/* DILEMMAS IN WAITING */}
      <div className="p-4 bg-[#0EF7B9] relative flex flex-col gap-y-2 isolate rounded-md overflow-hidden">
        <p>{t("modules.stats.stat_cards.pending_dilemmas")}</p>
        <span className="text-3xl font-bold">
          {data.kpis?.total_submitted_dilemmas.current}
        </span>
        <p className="text-sm flex items-center gap-1">
          {evolutionIcon(
            data.kpis?.total_submitted_dilemmas.previous,
            data.kpis?.total_submitted_dilemmas.current,
            data.kpis?.total_submitted_dilemmas.evolution_percent,
          )}
          {formatPercentage(
            Number(data.kpis?.total_submitted_dilemmas.evolution_percent),
          )}
        </p>
        <img src={StatNotebook} className="absolute bottom-0 right-0 -z-10" />
      </div>
      {/* ACTIVE USERS */}
      <div className="p-4 bg-[#FFA973] relative flex flex-col gap-y-2 isolate rounded-md overflow-hidden">
        <p>{t("modules.stats.stat_cards.active_users")}</p>
        <span className="text-3xl font-bold">
          {data.kpis?.total_active_users.current}
        </span>
        <p className="text-sm flex items-center gap-1">
          {evolutionIcon(
            data.kpis?.total_active_users.previous,
            data.kpis?.total_active_users.current,
            data.kpis?.total_active_users.evolution_percent,
          )}
          {formatPercentage(
            Number(data.kpis?.total_active_users.evolution_percent),
          )}
        </p>
        <img src={StatPerson} className="absolute bottom-0 right-0 -z-10" />
      </div>
    </div>
  );
};

export default StatCards;
